import Image from "next/image"
import type { ReactNode } from "react"
import { cmsMediaUrl } from "@/lib/cms"
import type { LexicalContent, LexicalNode } from "@/lib/cms"
import { isExternalHref } from "@/lib/url"

/* ───────────────────── Shared Lexical renderer ─────────────────────
   The single Lexical → React renderer used by both the insights article body
   (app/.../insights/[slug]/article-detail-client.tsx) and the landing-page
   `richText` block (components/cms/landing-blocks). It handles every node the
   CMS can emit — headings, lists, tables (with the <tbody> hydration fix),
   quotes, uploads, links + autolinks (pasted-URL hydration fix), inter-node
   space repair, and an inline-safe fallback for unknown nodes.

   Everything that differs per surface is injected through `LexicalRenderOptions`
   so the tree-walk stays identical:
   - `resolveHref` — how a link/autolink's raw href becomes a final URL. The
     article does dead-link rewriting + locale-prefixing; the landing block does
     `resolveHref(raw, prefix)` + `safeUrl`.
   - `stripText` — optional per-text-run transform. The article uses it to strip
     the inline image-placeholder tokens the editor injects; landing pages omit
     it. */

export interface LexicalRenderOptions {
  /** Resolve a link/autolink node's raw href (fields.url ?? url) to a final URL. */
  resolveHref: (rawHref: string) => string
  /**
   * Optional transform applied to every text run before it renders. The
   * insights body strips inline image-placeholder tokens here; landing pages
   * pass nothing (identity).
   */
  stripText?: (text: string) => string
}

// Lexical text `format` is a bitmask. Bold/italic/underline/strikethrough are
// the ones the CMS toolbar can set.
const FMT_BOLD = 1
const FMT_ITALIC = 2
const FMT_STRIKETHROUGH = 4
const FMT_UNDERLINE = 8

const INLINE_TYPES = new Set(["text", "link", "autolink"])

// A Lexical tree from Payload is always acyclic and only a handful of levels
// deep. Every recursive descent below carries a depth (or `path.length`); if it
// ever passes this the node graph is cyclic/corrupt — throw a DISTINCT, loud
// error rather than let it recurse into an unactionable "Maximum call stack size
// exceeded" (which we drop as third-party Sentry-replay noise; see
// instrumentation-client.ts). This keeps that message filter from ever masking a
// real recursion in this file — including the leaf-seeking helpers below, which
// walk sibling subtrees before the main guarded render descends into them.
const MAX_LEXICAL_DEPTH = 100

/** Trailing text of a node (for inter-node space detection). */
function trailingText(node: LexicalNode, depth = 0): string {
  if (depth > MAX_LEXICAL_DEPTH) {
    throw new Error(
      `trailingText: Lexical tree exceeded ${MAX_LEXICAL_DEPTH} levels — cyclic or corrupt CMS node`
    )
  }
  if (node.text) return node.text
  if (node.children?.length)
    return trailingText(node.children[node.children.length - 1], depth + 1)
  return ""
}

/** Leading text of a node (for inter-node space detection). */
function leadingText(node: LexicalNode, depth = 0): string {
  if (depth > MAX_LEXICAL_DEPTH) {
    throw new Error(
      `leadingText: Lexical tree exceeded ${MAX_LEXICAL_DEPTH} levels — cyclic or corrupt CMS node`
    )
  }
  if (node.text) return node.text
  if (node.children?.length) return leadingText(node.children[0], depth + 1)
  return ""
}

/**
 * Render a node's children, inserting a space between adjacent inline nodes
 * when neither side already has whitespace at the boundary. Lexical stores
 * "word <link>word</link>" as separate runs with the space living on whichever
 * side the editor split — without this repair, adjacent runs render glued
 * together.
 */
function renderBlockChildren(
  children: LexicalNode[],
  opts: LexicalRenderOptions,
  inCell: boolean,
  path: number[] = []
): ReactNode[] {
  const result: ReactNode[] = []
  for (let j = 0; j < children.length; j++) {
    if (
      j > 0 &&
      INLINE_TYPES.has(children[j].type) &&
      INLINE_TYPES.has(children[j - 1].type)
    ) {
      const prevEnd = trailingText(children[j - 1])
      const currStart = leadingText(children[j])
      if (
        prevEnd &&
        currStart &&
        !/\s$/.test(prevEnd) &&
        !/^\s/.test(currStart)
      ) {
        result.push(" ")
      }
    }
    result.push(renderLexicalNode(children[j], j, opts, inCell, [...path, j]))
  }
  return result
}

export function renderLexicalNode(
  node: LexicalNode,
  i: number,
  opts: LexicalRenderOptions,
  inCell = false,
  path: number[] = []
): ReactNode {
  if (path.length > MAX_LEXICAL_DEPTH) {
    throw new Error(
      `renderLexicalNode: Lexical tree exceeded ${MAX_LEXICAL_DEPTH} levels at path [${path.join(",")}] — cyclic or corrupt CMS node`
    )
  }
  // Inline nodes
  if (node.type === "text") {
    const text = opts.stripText
      ? opts.stripText(node.text ?? "")
      : (node.text ?? "")
    if (!text) return null
    let el: ReactNode = text
    const fmt = node.format ?? 0
    if (fmt & FMT_BOLD) el = <strong key={i}>{el}</strong>
    if (fmt & FMT_ITALIC) el = <em key={i}>{el}</em>
    if (fmt & FMT_UNDERLINE) el = <u key={i}>{el}</u>
    if (fmt & FMT_STRIKETHROUGH) el = <s key={i}>{el}</s>
    // Each text run is wrapped in a leaf <span> so the inline editor's injector
    // (injectRegionStamps) can align the in-memory Lexical tree to the rendered
    // DOM and stamp editable runs after a signed-in user opens the editor. No
    // SSR stamp ships in the public HTML.
    return <span key={i}>{el}</span>
  }
  // `autolink` is Lexical's pasted-URL variant — same shape as `link`, same
  // anchor render. Without this case it falls through to the block `default`
  // below and emits a block element inside the surrounding <p>, which the
  // browser parser silently repairs by closing the <p> early → React hydrates
  // against the un-repaired tree → "Hydration failed" (AHD / Sentry
  // MARKETING-SITE-N). The CMS emits autolink whenever an editor pastes a bare
  // URL, so this affected every article with a Sources section.
  if (node.type === "link" || node.type === "autolink") {
    const raw =
      (typeof node.fields?.url === "string" && node.fields.url) ||
      (typeof node.url === "string" && node.url) ||
      "#"
    const href = opts.resolveHref(raw)
    const external = isExternalHref(href)
    return (
      <a
        key={i}
        href={href}
        {...(external && { target: "_blank", rel: "noopener noreferrer" })}
        className="text-foreground decoration-foreground/40 hover:decoration-foreground/80 underline underline-offset-2 transition-colors"
      >
        {node.children?.map((c, j) =>
          renderLexicalNode(c, j, opts, false, [...path, j])
        )}
      </a>
    )
  }
  if (node.type === "linebreak") return <br key={i} />

  // Block nodes — render children with inter-node space repair
  const cellCtx = inCell || node.type === "tablecell"
  const kids = node.children
    ? renderBlockChildren(node.children, opts, cellCtx, path)
    : null
  const isEmpty = !kids || kids.every((k) => k === null)

  switch (node.type) {
    case "heading": {
      if (isEmpty) return null
      const Tag = (node.tag as "h1" | "h2" | "h3" | "h4" | "h5" | "h6") || "h2"
      const cls =
        Tag === "h2"
          ? "type-display-sm text-foreground mt-10 mb-4"
          : Tag === "h3"
            ? "type-headline-sm text-foreground mt-8 mb-3"
            : "type-headline-sm text-foreground mt-10 mb-4"
      return (
        <Tag key={i} className={cls}>
          {kids}
        </Tag>
      )
    }
    case "paragraph":
      if (isEmpty) return null
      if (inCell) return <span key={i}>{kids}</span>
      return (
        <p key={i} className="type-body-xl text-foreground mb-4">
          {kids}
        </p>
      )
    case "list":
      return node.listType === "number" ? (
        <ol
          key={i}
          className="type-body-xl text-foreground mb-4 list-decimal space-y-1 pl-6"
        >
          {kids}
        </ol>
      ) : (
        <ul
          key={i}
          className="type-body-xl text-foreground mb-4 list-disc space-y-1 pl-6"
        >
          {kids}
        </ul>
      )
    case "listitem":
      return <li key={i}>{kids}</li>
    case "quote":
      return (
        <blockquote
          key={i}
          className="border-aubergine-200 my-6 border-l-4 pl-4 text-neutral-600 italic"
        >
          {kids}
        </blockquote>
      )
    case "table":
      // Wrap rows in <tbody> so the server-rendered DOM matches the client's
      // hydration tree. Without this the browser's HTML parser silently inserts
      // a <tbody> around <tr> children in the static HTML, but React doesn't
      // reproduce that node during hydration — every article with a Lexical
      // table threw "Hydration failed".
      return (
        <figure key={i} className="my-8 overflow-auto">
          <table className="type-body-lg w-full border-collapse">
            <tbody>{kids}</tbody>
          </table>
        </figure>
      )
    case "tablerow":
      return <tr key={i}>{kids}</tr>
    case "tablecell": {
      // Lexical's TableCellHeaderStates is a bitmask: ROW = 1 (top header
      // row), COLUMN = 2 (first header column), BOTH = 3 (top-left cell).
      // Any non-zero state is a header cell — checking only === 1 left the
      // first-column headers and the top-left cell rendering as plain <td>s.
      const isHeader =
        node.headerState === 1 ||
        node.headerState === 2 ||
        node.headerState === 3
      const CellTag = isHeader ? "th" : "td"
      return (
        <CellTag
          key={i}
          className={
            isHeader
              ? // `[&_*]:text-white!` — CMS authors can apply any text style to
                // header-cell content (e.g. headings, which carry their own
                // text-foreground class that would beat the inherited white).
                // The important descendant override keeps every nested element
                // white on the aubergine background regardless of the style
                // applied in the editor.
                "bg-aubergine-800 px-4 py-3 text-left font-medium text-white [&_*]:text-white!"
              : "text-foreground border-t border-[rgba(153,153,153,0.25)] px-4 py-3" // ds-color-ignore: translucent overlay
          }
        >
          {kids}
        </CellTag>
      )
    }
    case "upload": {
      const media = node.value as { url?: string; alt?: string } | undefined
      if (media?.url) {
        return (
          <div key={i} className="my-6">
            <Image
              src={cmsMediaUrl(media.url)}
              alt={media.alt || ""}
              width={805}
              height={450}
              className="w-full"
            />
          </div>
        )
      }
      return null
    }
    default:
      // Inline-safe fallback. A <div> here would emit block-level markup inside
      // a <p> for any unknown node nested in a paragraph, which the HTML parser
      // repairs by closing the <p> early → desync from React's hydration tree →
      // "Hydration failed". <span> renders identically in body flow and is
      // legal inside <p>.
      return kids ? <span key={i}>{kids}</span> : null
  }
}

/** Render a whole Lexical document's top-level children. */
export function RenderLexical({
  content,
  options,
}: {
  content: LexicalContent
  options: LexicalRenderOptions
}) {
  return (
    <>
      {content.root.children?.map((node, i) =>
        renderLexicalNode(node, i, options, false, [i])
      )}
    </>
  )
}
